// Updated API service without adapter - direct camelCase communication
import AuthService from './authService';

export const API_BASE = import.meta.env.VITE_API_URL || 'https://api.kgcinnovations.com/api/v2';

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    // Get auth headers
    const authHeaders = AuthService.getAuthHeaders();
    // console.log('Making request to:', url, 'with auth headers:', authHeaders);

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders, // Add auth headers automatically
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // console.log('Response status:', response.status);

      // Try to parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid server response');
      }

      // Handle authentication errors
      if (response.status === 401) {
        console.log('401 Unauthorized - clearing auth and reloading');
        AuthService.clearAuth();
        // window.location.href = '/admin/login';
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        console.error('API Error:', data);
        throw new Error(data.message || data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Special request method for file uploads (doesn't set Content-Type header)
  async requestFile(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    // Get auth headers
    const authHeaders = AuthService.getAuthHeaders();

    const config = {
      headers: {
        ...authHeaders, // Add auth headers automatically
        ...options.headers,
        // Don't set Content-Type for file uploads - let browser set it with boundary
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        console.log('401 Unauthorized - clearing auth and reloading');
        AuthService.clearAuth();
        // window.location.href = '/admin/login';
        throw new Error('Authentication required');
      }

      // Try to parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        console.error('API Error:', data);
        throw new Error(data.message || data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Authentication
  async login(username, password) {
    return AuthService.login(username, password);
  }

  async logout() {
    return AuthService.logout();
  }

  async getCurrentUser() {
    return AuthService.verifyToken();
  }

  // Clients
  async getClients(params = {}) {
    const query = new URLSearchParams(params).toString();
    const endpoint = `/clients${query ? `?${query}` : ''}`;
    return this.request(endpoint);
  }

  async getClient(id) {
    return this.request(`/clients/${id}`);
  }

  async createClient(clientData) {
    // Create a copy of clientData and remove _id if it exists
    const { _id, ...cleanClientData } = clientData;
    
    return this.request('/clients', {
      method: 'POST',
      body: cleanClientData,
    });
  }

  async updateClient(id, clientData) {
    return this.request(`/clients/${id}`, {
      method: 'PUT',
      body: clientData,
    });
  }

  async deleteClient(id, force = false) {
    const endpoint = `/clients/${id}${force ? '?force=true' : ''}`;
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Client Documents
  async getClientDocuments(clientId) {
    return this.request(`/client-documents/client/${clientId}`);
  }

  async uploadDocument(clientId, file, documentInfo) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientId', clientId);
    formData.append('label', documentInfo.label);
    formData.append('description', documentInfo.description || '');
    formData.append('isConfidential', documentInfo.isConfidential || false);

    return this.requestFile(`/client-documents?type=client-document`, {
      method: 'POST',
      body: formData
    });
  }

  async getDocument(documentId) {
    return this.request(`/client-documents/${documentId}`);
  }

  async updateDocument(documentId, updates) {
    return this.request(`/client-documents/${documentId}`, {
      method: 'PUT',
      body: updates
    });
  }

  async deleteDocument(documentId) {
    return this.request(`/client-documents/${documentId}`, {
      method: 'DELETE'
    });
  }

  // Download document (this will trigger browser download)
  async downloadDocument(documentId) {
    try {
      const authHeaders = AuthService.getAuthHeaders();
      const response = await fetch(`${API_BASE}/client-documents/download/${documentId}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      const filename = data.data.fileName || 'document';

      // Ddownload
      const url = data.data.fileUrl;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank'; // Opens in new tab if download fails
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  // Invoices
  async getInvoices(params = {}) {
    const query = new URLSearchParams(params).toString();
    const endpoint = `/invoices${query ? `?${query}` : ''}`;
    return this.request(endpoint);
  }

  async getInvoice(id) {
    return this.request(`/invoices/${id}`);
  }

  async createInvoice(invoiceData) {
    // A new record has no id yet; the form's empty _id would fail to cast.
    const { _id, id, ...cleanInvoiceData } = invoiceData;
    return this.request('/invoices', {
      method: 'POST',
      body: cleanInvoiceData,
    });
  }

  async updateInvoice(id, invoiceData) {
    return this.request(`/invoices/${id}`, {
      method: 'PUT',
      body: invoiceData,
    });
  }

  async deleteInvoice(id, force = false) {
    const endpoint = `/invoices/${id}${force ? '?force=true' : ''}`;
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Quotes
  async getQuotes(params = {}) {
    const query = new URLSearchParams(params).toString();
    const endpoint = `/quotes${query ? `?${query}` : ''}`;
    return this.request(endpoint);
  }

  async getQuote(id) {
    return this.request(`/quotes/${id}`);
  }

  async createQuote(quoteData) {
    const { _id, id, ...cleanQuoteData } = quoteData;
    return this.request('/quotes', {
      method: 'POST',
      body: cleanQuoteData,
    });
  }

  async updateQuote(id, quoteData) {
    return this.request(`/quotes/${id}`, {
      method: 'PUT',
      body: quoteData,
    });
  }

  async deleteQuote(id) {
    return this.request(`/quotes/${id}`, { method: 'DELETE' });
  }

  // Payments
  async getPayments(params = {}) {
    const query = new URLSearchParams(params).toString();
    const endpoint = `/payments${query ? `?${query}` : ''}`;
    return this.request(endpoint);
  }

  async getPaymentsForInvoice(invoiceId) {
    return this.request(`/payments/invoice/${invoiceId}`);
  }

  async recordPayment(paymentData) {
    return this.request('/payments', {
      method: 'POST',
      body: paymentData,
    });
  }

  async updatePayment(id, paymentData) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: paymentData,
    });
  }

  async deletePayment(id) {
    return this.request(`/payments/${id}`, { method: 'DELETE' });
  }

  // Settings
  async getSettings() {
    return this.request('/company-settings');
  }

  async updateSettings(settingsData) {
    return this.request('/company-settings', {
      method: 'PUT',
      body: settingsData,
    });
  }

  async getUserPermissions() {
    return this.request('/user-permissions');
  }

  async updateUserPermissions(permissionsData) {
    return this.request('/user-permissions', {
      method: 'PUT',
      body: permissionsData,
    });
  }

  async resetUserPermissions() {
    return this.request('/user-permissions/reset', {
      method: 'POST'
    });
  }

  // Payment Providers
  async getPaymentProviders() {
    return this.request('/payments/providers');
  }

  async getPaymentProvider(id) {
    return this.request(`/payments/providers/${id}`);
  }

  async createPaymentProvider(providerData) {
    return this.request('/payments/providers', {
      method: 'POST',
      body: providerData,
    });
  }

  async updatePaymentProvider(id, providerData) {
    return this.request(`/payments/providers/${id}`, {
      method: 'PUT',
      body: providerData,
    });
  }

  async deletePaymentProvider(id) {
    return this.request(`/payments/providers/${id}`, {
      method: 'DELETE',
    });
  }

  // Test SMTP connection
  async testEmailConnection() {
    return this.request('/emails/test-connection', {
      method: 'GET'
    });
  }

  // Send invoice email
  async sendInvoiceEmail(invoiceId, emailData) {
    return this.request('/emails/send-email', {
      method: 'POST',
      body: {
        invoiceId,
        type: 'invoice',
        recipientEmail: emailData.recipientEmail || '',
        recipientName: emailData.recipientName || '',
        subject: emailData.subject || '',
        message: emailData.message || '',
        includeAttachment: emailData.includeAttachment !== false
      }
    });
  }

  // Send quote email  
  async sendQuoteEmail(quoteId, emailData) {
    return this.request('/emails/send-email', {
      method: 'POST',
      body: {
        quoteId,
        type: 'quote',
        recipientEmail: emailData.recipientEmail,
        recipientName: emailData.recipientName || '',
        subject: emailData.subject || '',
        message: emailData.message || '',
        includeAttachment: emailData.includeAttachment !== false
      }
    });
  }

  // Reports
  //
  // The report figures are already computed in the page, so the API turns them
  // straight into a file and returns the bytes — one request, no temp files on
  // the server to generate then fetch.
  async exportReport(reportData, reportType, exportFormat, dateRange) {
    const response = await fetch(`${API_BASE}/reports/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...AuthService.getAuthHeaders(),
      },
      body: JSON.stringify({ reportData, reportType, exportFormat, dateRange }),
    });

    if (!response.ok) {
      let message = 'Export failed';
      try {
        const error = await response.json();
        message = error.message || error.error || message;
      } catch {
        // Non-JSON error body; keep the default message.
      }
      throw new Error(message);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    const match = contentDisposition && contentDisposition.match(/filename="(.+)"/);
    const filename = match ? match[1] : `${reportType}-report`;

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, data: { filename } };
  }

  // Health check
  async checkHealth() {
    return this.request('/health');
  }

  // Admin impersonation - login as client
  async impersonateClient(clientId) {
    return this.request('/auth/impersonate', {
      method: 'POST',
      body: { clientId }
    });
  }

  // Admin Payment Processing
  async getClientPaymentOptions(clientId, invoiceId = null) {
    const params = new URLSearchParams({
      action: 'payment_options',
      client_id: clientId
    });
    
    if (invoiceId) {
      params.append('invoice_id', invoiceId);
    }

    return this.request(`/payments/get-payment-methods/${clientId}?${params.toString()}`);
  }

  async processAdminPayment(paymentData) {
    return this.request('/payments/process-admin-payment', {
      method: 'POST',
      body: {
        action: 'process_payment',
        ...paymentData
      }
    });
  }
}

export default new ApiService();